import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronDown, Clock, Phone, Video } from 'lucide-react';
import { eventsAPI } from '../api/client';
import { useCalendarConnected } from '../hooks/useCalendarConnected';
import EventCalendarAction, { ConnectCalendarHint } from './EventCalendarAction';
import EventRsvpButtons from './EventRsvpButtons';
import { AddressLink } from './OpenInMapsModal';
import InsetItem, { InsetMeta } from './InsetItem';
import type { UpcomingEvent } from '../types';

function formatTimeRange(startIso: string, endIso: string): string {
  const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  const start = new Date(startIso).toLocaleTimeString(undefined, opts);
  const end = new Date(endIso).toLocaleTimeString(undefined, opts);
  return `${start} – ${end}`;
}

function dateParts(iso: string) {
  const date = new Date(iso);
  return {
    weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
    month: date.toLocaleDateString(undefined, { month: 'short' }),
    day: date.getDate(),
  };
}

function isOneOnOne(event: UpcomingEvent): boolean {
  return event.kind === 'member_meeting';
}

function eventLabel(event: UpcomingEvent): string {
  return isOneOnOne(event) ? event.title : (event.forum_name || event.title);
}

function eventHref(event: UpcomingEvent): string {
  if (isOneOnOne(event)) return '/meetings';
  return event.forum ? `/forum?forum=${event.forum}` : '/forum';
}

const MODE_LABELS: Record<string, string> = {
  in_person: 'In person',
  phone: 'Phone',
  video: 'Video',
  other: 'Other',
};

/** Next forum meeting per forum, plus every 1:1 in the fetched window. */
export function selectComingUpEvents(events: UpcomingEvent[]): UpcomingEvent[] {
  const nextByForum = new Map<number, UpcomingEvent>();
  const ones: UpcomingEvent[] = [];

  for (const event of events) {
    if (isOneOnOne(event)) {
      ones.push(event);
      continue;
    }
    if (!event.forum) continue;
    const existing = nextByForum.get(event.forum);
    if (!existing || new Date(event.starts_at) < new Date(existing.starts_at)) {
      nextByForum.set(event.forum, event);
    }
  }

  return [...nextByForum.values(), ...ones].sort(
    (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
  );
}

function MeetingLinks({ event }: { event: UpcomingEvent }) {
  if (isOneOnOne(event)) {
    const mode = event.meeting_mode || '';
    const place = (event.meeting_place || '').trim();
    const videoUrl = (event.conference_url || '').trim();
    return (
      <>
        {mode && <InsetMeta>{MODE_LABELS[mode] || mode}</InsetMeta>}
        {mode === 'video' && videoUrl && (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-navy hover:text-gold hover:underline"
          >
            <Video size={13} />
            Join video
          </a>
        )}
        {mode === 'phone' && place && (
          <a
            href={`tel:${place}`}
            className="inline-flex items-center gap-1 text-navy hover:text-gold hover:underline"
          >
            <Phone size={13} />
            {place}
          </a>
        )}
        {mode === 'in_person' && place && (
          <AddressLink address={place} label={event.title} className="basis-full" />
        )}
        {(mode === 'other' || (!['video', 'phone', 'in_person'].includes(mode))) && place && (
          <InsetMeta>{place}</InsetMeta>
        )}
      </>
    );
  }

  const place = event.venue_name || event.location;
  if (!place) return null;
  return (
    <AddressLink
      address={event.location}
      label={event.venue_name || event.forum_name || event.title}
      className="basis-full"
    />
  );
}

export default function ComingUpCard() {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const { connected, loading: calendarLoading } = useCalendarConnected();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await eventsAPI.getUpcoming({ days: 60, mine: true });
        if (!cancelled) setEvents(data);
      } catch {
        // Non-blocking: the card simply renders empty if events can't load.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => selectComingUpEvents(events), [events]);

  useEffect(() => {
    if (!listOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (!listRef.current?.contains(event.target as Node)) {
        setListOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setListOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [listOpen]);

  const preview = visible.slice(0, 3);

  return (
    <div className="bg-white rounded-2xl px-6 py-5 shadow-sm h-full flex flex-col">
      <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-4">
        Coming up
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : preview.length === 0 ? (
        <div className="flex-1 flex flex-col">
          <p className="text-lg font-semibold text-navy">No meetings in the next 60 days</p>
          <p className="text-sm text-gray-500 mt-1">
            Forum meetings and 1:1s you are on will show up here.
          </p>
        </div>
      ) : (
        <div className="flex-1 space-y-3">
          {preview.map((event) => {
            const parts = dateParts(event.starts_at);
            return (
              <InsetItem
                key={event.occurrence_id}
                title={
                  <>
                    <Link to={eventHref(event)} className="hover:underline">
                      {eventLabel(event)}
                    </Link>
                    {isOneOnOne(event) && (
                      <span className="text-gray-400 font-normal"> · 1:1</span>
                    )}
                  </>
                }
                meta={
                  <>
                    <InsetMeta icon={Calendar}>
                      {parts.weekday} {parts.month} {parts.day}
                    </InsetMeta>
                    <InsetMeta icon={Clock}>
                      {formatTimeRange(event.starts_at, event.ends_at)}
                    </InsetMeta>
                    <MeetingLinks event={event} />
                  </>
                }
                action={
                  <div className="flex flex-col items-end gap-1.5">
                    <EventRsvpButtons event={event} />
                    <EventCalendarAction
                      icsUrl={event.ics_url}
                      links={event.calendar_links}
                      filename={`${event.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'event'}.ics`}
                      connected={connected}
                      variant="ghost"
                    />
                  </div>
                }
              />
            );
          })}
        </div>
      )}

      <div className="mt-auto pt-5 flex flex-wrap items-center justify-between gap-2 text-sm">
        <ConnectCalendarHint connected={connected} loading={calendarLoading} compact />
        {visible.length > preview.length && (
          <div className="relative ml-auto" ref={listRef}>
            <button
              type="button"
              onClick={() => setListOpen((open) => !open)}
              aria-expanded={listOpen}
              aria-haspopup="listbox"
              className="inline-flex items-center gap-1 text-navy hover:underline"
            >
              All meetings
              <ChevronDown
                size={14}
                className={`transition-transform ${listOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {listOpen && (
              <div
                role="listbox"
                className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-3rem)] bg-white border border-gray-200 rounded-xl shadow-lg z-30 max-h-72 overflow-y-auto py-1"
              >
                {visible.map((event) => {
                  const extra = dateParts(event.starts_at);
                  return (
                    <Link
                      key={event.occurrence_id}
                      to={eventHref(event)}
                      role="option"
                      className="block px-3 py-2.5 hover:bg-off-white"
                      onClick={() => setListOpen(false)}
                    >
                      <p className="text-sm text-navy leading-snug">
                        {eventLabel(event)}
                        {isOneOnOne(event) && (
                          <span className="text-gray-400 font-normal"> · 1:1</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {extra.weekday} {extra.month} {extra.day}
                        {' · '}
                        {formatTimeRange(event.starts_at, event.ends_at)}
                      </p>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
